import { useRef, useEffect, useCallback } from 'react'

const isOnScreen = (el) => {
  const rect = el.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > 0
}

const useObserver = (location) => {
  const pathname = location?.pathname
  const observerRef = useRef(null)

  const initializeObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    const reveal = (el) => {
      el.classList.add('visible')
      observerRef.current.unobserve(el)
    }

    observerRef.current = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
            reveal(entry.target)
          }
        })
      },
      { threshold: [0, 0.1], rootMargin: '0px 0px -1px 0px' }
    )

    const elements = [...document.querySelectorAll('.fadeIn, .fadeInFromBottom')]
    elements.forEach((el) => {
      el.classList.remove('visible')
      observerRef.current.observe(el)
    })

    return () => elements.forEach((el) => {
      if (!el.classList.contains('visible') && isOnScreen(el)) reveal(el)
    })
  }, [])

  useEffect(() => {
    const sweep = initializeObserver()
    const frame = window.requestAnimationFrame(sweep)
    const timeouts = [300, 1000].map((delay) => window.setTimeout(sweep, delay))

    return () => {
      window.cancelAnimationFrame(frame)
      timeouts.forEach((timeout) => window.clearTimeout(timeout))
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [pathname, initializeObserver])
}

export default useObserver
